import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { useMutation } from "@tanstack/react-query";
import { auditIrreversibleReason, auditRevertibility, auditUndo, type AuditEntryView, type AuditNoteState, type AuditRevertibility } from "@starsach/shared-types";
import { useAccess, useAudit, useAuditUndo, useRevert, useRevertPreview } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { useStepUp } from "../../auth/StepUpProvider";
import { NotHeld } from "../../components/NotHeld";
import { Hint } from "../../components/Hint";
import { Card, DataTable, Dialog, EmptyState, ErrorState, LoadingState, MutationError, RelativeTime, StatusPill, btnPrimary, btnSecondary } from "../../components/ui";
import { actionLabel } from "../../lib/audit-labels";

const REVERT_TONE: Record<AuditRevertibility, "ok" | "warning" | "neutral"> = {
  revertible: "ok",
  "reverse where made": "neutral",
  "not reversible": "warning",
  "nothing changed": "neutral",
};

const AUTHORITY: Record<AuditEntryView["authority"], { label: string; tone: "ok" | "warning" | "critical" | "neutral" }> = {
  not_required: { label: "no approval needed", tone: "neutral" },
  approved: { label: "approved", tone: "ok" },
  self_approved: { label: "self-approved", tone: "warning" },
  break_glass: { label: "break glass", tone: "critical" },
};

/**
 * The audit trail (requirements.md section 11). The limit on what it can
 * possibly contain is stated here rather than only in documentation
 * (11.1) - an empty log means the portal changed nothing, not that
 * nothing changed, and someone reading it as a complete history of their
 * contact center will draw a false conclusion at the worst moment.
 */
export function AuditTab({ environmentId }: { environmentId: string }) {
  const [scope, setScope] = useState<"environment" | "organisation">("environment");
  const [expanded, setExpanded] = useState<string>();
  const q = useAudit(environmentId, scope);
  const note = useAuditNote();
  const restoreRef = useRef<HTMLButtonElement>(null);
  const noteToggleRef = useRef<HTMLButtonElement>(null);
  // Where focus goes after the note is removed or brought back, so a keyboard user is never left on a control that vanished.
  const [focusNext, setFocusNext] = useState<"restore" | "note">();
  useEffect(() => {
    if (focusNext === "restore") restoreRef.current?.focus();
    if (focusNext === "note") noteToggleRef.current?.focus();
    if (focusNext) setFocusNext(undefined);
  }, [focusNext]);

  return (
    <div className="space-y-4">
      <Card
        title={
          <>
            Changes made through the portal<Hint id="audit.limits" label="what the trail can and cannot show" />
            {note.state === "removed" && (
              <button
                ref={restoreRef}
                type="button"
                className="ml-3 text-xs font-normal normal-case tracking-normal text-accent underline hover:no-underline"
                onClick={() => {
                  note.set("shown");
                  setFocusNext("note");
                }}
              >
                About this list
              </button>
            )}
          </>
        }
      >
        <fieldset className="mb-3">
          <legend className="sr-only">Which changes to show</legend>
          <div className="inline-flex rounded border border-neutral-300 dark:border-neutral-700">
            {(["environment", "organisation"] as const).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={scope === s}
                onClick={() => setScope(s)}
                className={`px-3 py-1.5 text-sm ${scope === s ? "bg-accent text-white" : "hover:bg-neutral-100 dark:hover:bg-neutral-800"}`}
              >
                {s === "environment" ? "This environment" : "Everything I can see"}
              </button>
            ))}
          </div>
        </fieldset>

        {q.isPending && <LoadingState label="Loading the audit trail" />}
        {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
        {q.data && (
          <DataTable
            caption={scope === "environment" ? "Changes the portal made to this environment, newest first" : "Every change the portal made that you can see, newest first"}
            rows={q.data.entries.map((e) => ({ ...e, revertibility: auditRevertibility(e) }))}
            rowKey={(e) => e.id}
            empty={
              <EmptyState
                title="The portal has not changed anything here"
                hint="That is not the same as nothing having changed. A change made in the Connect or AWS console does not appear here and cannot."
              />
            }
            columns={[
              { key: "when", header: "When", render: (e) => <RelativeTime iso={e.timestamp} /> },
              { key: "what", header: "Action", render: (e) => <span title={e.action}>{actionLabel(e.action)}</span> },
              {
                key: "who", header: "Who",
                render: (e) => (
                  <>
                    {e.actorName ? <>{e.actorName} <span className="text-neutral-500">({e.actor})</span></> : e.actor}
                    {/* The distinction that gets asked for first after any incident. */}
                    {e.actorIsInternal && <span className="ml-1 text-xs text-neutral-500">{e.viewAs ? "(Gairm staff, in your portal)" : "(Gairm staff)"}</span>}
                  </>
                ),
              },
              {
                key: "authority", header: "Authority",
                render: (e) => (
                  <>
                    <StatusPill status={AUTHORITY[e.authority].label} tone={AUTHORITY[e.authority].tone} />
                    {e.approvedBy && <span className="ml-1 text-xs text-neutral-500">by {e.approvedBy}</span>}
                  </>
                ),
              },
              {
                key: "result", header: "Result",
                render: (e) => <StatusPill status={e.result} tone={e.result === "applied" ? "ok" : e.result === "denied" ? "critical" : "warning"} />,
              },
              {
                key: "revert", header: <>Revert<Hint id="audit.revertible" label="whether a change can be reverted here" /></>,
                render: (e) => (
                  <>
                    <StatusPill status={e.revertibility} tone={REVERT_TONE[e.revertibility]} />
                    {/* 11.6: a one-way change says why, rather than offering nothing. */}
                    {e.revertibility === "not reversible" && <span className="mt-1 block max-w-60 text-xs text-neutral-600 dark:text-neutral-400">{auditIrreversibleReason(e.action)}</span>}
                  </>
                ),
              },
              { key: "where", header: "Environment", render: (e) => e.environmentId ?? <span className="text-neutral-500">organization</span> },
              {
                key: "changes", header: "",
                render: (e) =>
                  e.changes.length === 0 ? null : (
                    <button type="button" className="text-sm underline" aria-haspopup="dialog" onClick={() => setExpanded(e.id)}>
                      {e.changes.length} field{e.changes.length === 1 ? "" : "s"}
                    </button>
                  ),
              },
            ]}
          />
        )}

        {/* The diff, in the portal and not only in the promotion screen (10.5) - in a dialog, where it is seen (the owner's walk, 2026-09-21), with the revert 11.6 promises. */}
        {q.data?.entries.filter((e) => e.id === expanded).map((e) => (
          <ChangeDialog key={e.id} environmentId={environmentId} entry={e} onClose={() => setExpanded(undefined)} />
        ))}
      </Card>

      {note.state !== "removed" && (
        <Limits
          minimized={note.state === "minimized"}
          toggleRef={noteToggleRef}
          onToggle={() => note.set(note.state === "minimized" ? "shown" : "minimized")}
          onRemove={() => {
            note.set("removed");
            setFocusNext("restore");
          }}
        />
      )}
      <MutationError of={note.save} />
    </div>
  );
}

/**
 * One entry's changes, and the way back. A profile entry can be reverted
 * from here (11.6): the preview shows what each field would go back to,
 * and the current value where the field has moved since; the revert is
 * a save through the same gate as any change, so in a gated environment
 * a non-manager's revert is sent for approval rather than refused.
 */
function ChangeDialog({ environmentId, entry, onClose }: { environmentId: string; entry: AuditEntryView; onClose: () => void }) {
  const profileId = typeof entry.details?.["profile_id"] === "string" ? (entry.details["profile_id"] as string) : undefined;
  // #242: an environment switch, the chain or a membership is undone as a whole; a profile's configuration is reverted field by field.
  const undo = auditUndo(entry);
  // Only the profile's own configuration is reverted field by field; an override or a binding is reversed where it was made.
  const couldRevert = auditRevertibility(entry) === "revertible" && !undo;
  // ADR-0017: undo is a verb of its own; without it the diff is read and nothing is offered.
  const canUndo = useAccess().can(environmentId, "audit:undo");
  const revertible = couldRevert && canUndo;
  const preview = useRevertPreview(environmentId, revertible ? profileId : undefined, revertible ? entry.id : undefined);
  const revert = useRevert(environmentId);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(entry.changes.filter((c) => !c.kept_target_value).map((c) => c.field)));
  const toggle = (field: string) => setChosen((prev) => { const next = new Set(prev); if (next.has(field)) next.delete(field); else next.add(field); return next; });
  const all = entry.changes.filter((c) => !c.kept_target_value).map((c) => c.field);
  const moved = new Map((preview.data?.fields ?? []).map((f) => [f.field, f]));
  const done = revert.isSuccess ? revert.data : undefined;

  return (
    <Dialog title={`${actionLabel(entry.action)} · ${entry.actorName ? `${entry.actorName} (${entry.actor})` : entry.actor}`} onClose={onClose} wide>
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        <RelativeTime iso={entry.timestamp} />
        {entry.requestId && <span className="ml-2 text-xs">request {entry.requestId}</span>}
        {entry.rolledBackFrom && <span className="ml-2 text-xs">restored from an earlier entry</span>}
      </p>
      <ul className="mt-3 space-y-3">
        {entry.changes.map((c) => {
          const m = moved.get(c.field);
          return (
            <li key={c.field} className="text-sm">
              <div className="flex flex-wrap items-center gap-2">
                {revertible && !done && (
                  <input type="checkbox" checked={chosen.has(c.field)} disabled={Boolean(c.kept_target_value)} aria-label={`Revert ${c.field}`} onChange={() => toggle(c.field)} />
                )}
                <code className="text-xs">{c.field}</code>
                {m?.moved && <StatusPill status="changed again since" tone="warning" />}
              </div>
              {c.kept_target_value ? (
                <span className="ml-6 text-neutral-600 dark:text-neutral-400">kept this environment's value</span>
              ) : (
                <div className={`mt-1 grid gap-2 ${m?.moved ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                  <div className="rounded bg-red-50 p-2 dark:bg-red-950">
                    <span className="block text-xs text-neutral-500">before{revertible ? " (a revert restores this)" : undo ? " (an undo restores this)" : ""}</span>
                    {format(c.before)}
                  </div>
                  <div className="rounded bg-green-50 p-2 dark:bg-green-950">
                    <span className="block text-xs text-neutral-500">after</span>
                    {format(c.after)}
                  </div>
                  {m?.moved && (
                    <div className="rounded bg-amber-50 p-2 dark:bg-amber-950">
                      <span className="block text-xs text-neutral-500">now - a revert would overwrite this</span>
                      {format(m.current)}
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {revertible && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {done ? (
            <p role="status" className="text-sm">
              {done.status === "published" ? `Reverted as v${done.configVersion}.` : `Sent for a manager's approval (${done.promotionId}).`}
            </p>
          ) : (
            <>
              <button type="button" className={btnPrimary} disabled={chosen.size === 0 || revert.isPending || preview.isPending} onClick={() => revert.mutate({ profileId: profileId!, entryId: entry.id, ...(chosen.size < all.length ? { fields: [...chosen] } : {}) })}>
                {revert.isPending ? "Reverting…" : preview.data?.saveAuthority === "needs_approval" ? `Request a revert of ${chosen.size === all.length ? "every field" : `${chosen.size} field${chosen.size === 1 ? "" : "s"}`}` : `Revert ${chosen.size === all.length ? "every field" : `${chosen.size} field${chosen.size === 1 ? "" : "s"}`}`}
              </button>
              <button type="button" className={`${btnSecondary} min-h-8 px-2.5 text-xs`} onClick={() => setChosen(new Set(chosen.size === all.length ? [] : all))}>
                {chosen.size === all.length ? "Select none" : "Select all"}
              </button>
              <Hint id="audit.revert" label="what a revert does" />
            </>
          )}
          <MutationError of={revert} className="basis-full" />
        </div>
      )}
      {couldRevert && !canUndo && <NotHeld permission="audit:undo" className="mt-4 block" />}
      {undo && <UndoAction environmentId={entry.environmentId ?? environmentId} entryId={entry.id} kind={undo.kind} needs={undo.needs} />}
      {!couldRevert && !undo && entry.changes.length > 0 && (
        <p className="mt-4 text-xs text-neutral-600 dark:text-neutral-400">
          {auditRevertibility(entry) === "not reversible"
            ? auditIrreversibleReason(entry.action)
            : "Undone from the audit trail: a profile's configuration, an environment's switches, the chain's order and roles held. This one is reversed where it was made."}
        </p>
      )}
      <div className="mt-4">
        <button type="button" className={btnSecondary} onClick={onClose}>Close</button>
      </div>
    </Dialog>
  );
}

/**
 * #242: an environment switch, the chain's order or a membership, undone from
 * its entry. Offered only when the person holds `audit:undo` and the verb the
 * change needed, on the environment the entry belongs to (or this one, for an
 * organization-level entry); a membership asks for a fresh sign-in first, as
 * granting does. The server re-checks all of it and refuses a field that has
 * changed since, which the error line reports.
 */
function UndoAction({ environmentId, entryId, kind, needs }: { environmentId: string; entryId: string; kind: string; needs: string }) {
  const access = useAccess();
  const { ensureFresh } = useStepUp();
  const undo = useAuditUndo(environmentId);
  const missing = ["audit:undo", needs].find((p) => !access.can(environmentId, p));
  if (missing) return <NotHeld permission={missing} className="mt-4 block" />;
  const go = () => undo.mutate(entryId);
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {undo.isSuccess ? (
        <p role="status" className="text-sm">Undone. The trail records it as Rolled back, naming this entry.</p>
      ) : (
        <>
          <button type="button" className={btnPrimary} disabled={undo.isPending} onClick={() => (kind === "membership" ? void ensureFresh().then(go, () => undefined) : go())}>
            {undo.isPending ? "Undoing…" : "Undo this change"}
          </button>
          <Hint id="audit.undo" label="what an undo does" />
        </>
      )}
      <MutationError of={undo} className="basis-full" />
    </div>
  );
}

/**
 * The note's state is the person's own preference (#402): shown on first
 * access, then minimized or removed until they bring it back - on any device.
 * The choice shows at once; a save that fails puts it back and says so.
 */
function useAuditNote() {
  const { preferences, updatePreferences } = useAuth();
  const [local, setLocal] = useState<AuditNoteState>();
  const save = useMutation({
    mutationFn: (auditNoteState: AuditNoteState) => updatePreferences({ auditNoteState }),
    onError: () => setLocal(undefined),
  });
  return {
    state: local ?? preferences.auditNoteState,
    save,
    set: (next: AuditNoteState) => {
      setLocal(next);
      save.mutate(next);
    },
  };
}

/**
 * Section 11.1: what the trail cannot contain, stated on the page - an empty
 * trail read as a complete history is the dangerous misreading. It sits below
 * the trail, because what needs someone comes first (the owner's rule of
 * 2026-09-21, #313), and floats at the bottom of the window while a long
 * trail scrolls (#402); being sticky rather than fixed, it takes its own room
 * at the end, so it never covers the last rows. The same limit is on the
 * card's title as a hint, which is where it is read before the rows are.
 */
function Limits({ minimized, toggleRef, onToggle, onRemove }: { minimized: boolean; toggleRef: RefObject<HTMLButtonElement>; onToggle: () => void; onRemove: () => void }) {
  const bodyId = useId();
  return (
    <div role="note" aria-label="What this can and cannot show" className="sticky bottom-3 z-10 rounded-lg border border-neutral-300 bg-neutral-50 p-3 text-sm shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
      <div className="flex items-center gap-2">
        <button ref={toggleRef} type="button" aria-expanded={!minimized} aria-controls={bodyId} onClick={onToggle} className="flex min-h-8 flex-1 items-center gap-2 rounded text-left font-medium">
          <svg aria-hidden="true" viewBox="0 0 24 24" className={`h-4 w-4 flex-none stroke-current transition-transform ${minimized ? "-rotate-90" : ""}`} fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
          What this can and cannot show
        </button>
        <button type="button" aria-label="Remove this note" onClick={onRemove} className="inline-flex h-8 w-8 flex-none items-center justify-center rounded text-neutral-600 hover:bg-neutral-200 hover:text-ink dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white">
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 stroke-current" fill="none" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>
      <div id={bodyId} hidden={minimized}>
      <p className="mt-1 text-neutral-700 dark:text-neutral-300">
        This records changes <strong>the portal made</strong>, including changes it made in your AWS account on your
        behalf. It does not record changes someone made directly in the Connect console, the AWS console or by CLI - the
        portal cannot see those. An empty list means the portal changed nothing, not that nothing changed.
      </p>
      <p className="mt-1 text-neutral-700 dark:text-neutral-300">
        CloudTrail in your own AWS account is the complete record of out-of-band change. Where the portal <em>can</em>{" "}
        detect one - a queue renamed or a flow deleted outside it - it appears as inventory drift on the flow
        configuration screen.
      </p>
      </div>
    </div>
  );
}

function format(value: unknown) {
  if (value === undefined) return <span className="text-neutral-500">not set</span>;
  if (typeof value === "string") return <span className="break-words">{value}</span>;
  return <code className="block break-words text-xs">{JSON.stringify(value)}</code>;
}
